import type { RuleHistoryEntry } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { formatDateTime } from '../lib/time'

type Fields = Record<string, unknown>
const fieldsOf = (value: unknown): Fields => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Fields : {}

/** What a stored value reads as in a revision: a switch as on or off, a list
 *  by its members, and a whole definition not at all (it is long; the line
 *  says it changed). */
export function revisionWord(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'none'
  if (typeof value === 'boolean') return value ? 'on' : 'off'
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'none'
  if (typeof value === 'object') return 'changed'
  return String(value)
}

/** The verb for a history row. The audit keeps one `update` action; a row
 *  whose only change is the switch reads as switched on or off. */
export function revisionVerb(entry: RuleHistoryEntry): string {
  const kind = entry.action.split('.').pop() ?? entry.action
  if (kind === 'create') return 'created'
  if (kind === 'delete') return 'deleted'
  if (kind !== 'update') return kind
  const after = fieldsOf(entry.detail.after)
  const keys = Object.keys(after)
  if (keys.length === 1 && keys[0] === 'enabled') return after.enabled ? 'switched on' : 'switched off'
  return 'changed'
}

/** A rule's or strategy's revisions: every create, change, switch on or off
 *  and delete, with what each field was and what it became, read from the
 *  server's own audit rows (GET .../history, for whoever manages the rule).
 *  `refresh` changes whenever the list should be read again. */
export function RuleRevisions({ load, refresh }: { load: () => Promise<{ history: RuleHistoryEntry[] }>; refresh: unknown }) {
  const log = useAsync(load, [refresh])
  const entries = log.data?.history ?? []
  return <details className="aw-revisions">
    <summary>Revisions {log.data ? <span className="count">({entries.length})</span> : null}</summary>
    {log.loading && <p className="muted small">Loading…</p>}
    {log.error && <p className="error small" role="alert">{log.error}</p>}
    {log.data && entries.length === 0 && <p className="muted small">No change recorded yet.</p>}
    {entries.length > 0 && <ol>{entries.map((entry, index) => {
      const before = fieldsOf(entry.detail.before), after = fieldsOf(entry.detail.after)
      const fields = Object.keys(after)
      const verb = revisionVerb(entry)
      return <li key={`${entry.at}-${index}`}>
        <span className="mono">{formatDateTime(entry.at)}</span> · {entry.actor} · <strong>{verb}</strong>
        {fields.length > 0 && verb === 'changed' && <ul>{fields.map(field => <li key={field}>
          {field.replace(/_/g, ' ')}: {revisionWord(before[field])} → {revisionWord(after[field])}</li>)}</ul>}
      </li>
    })}</ol>}
  </details>
}
